import React, { useState, useEffect } from 'react';
import { Search, Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const submitSearch = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setMobileMenuOpen(false);
    document.body.style.overflow = 'unset';
    navigate(`/shop?q=${encodeURIComponent(q)}`);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setMobileMenuOpen(false);
    document.body.style.overflow = 'unset';
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((open) => {
      const next = !open;
      document.body.style.overflow = next ? 'hidden' : 'unset';
      return next;
    });
  };

  // Always release the scroll lock on unmount.
  useEffect(() => () => { document.body.style.overflow = 'unset'; }, []);

  const navigationLinks = [
    { label: "Home", path: "/" },
    { label: "Shop", path: "/shop" },
    { label: "Artists", path: "/artists" },
    { label: "Sunday B. Morning", path: "/sunday-b-morning", featured: true },
    { label: "Marilyn at 100", path: "/marilyn-monroe", featured: true },
    { label: "News", path: "/news" },
    { label: "Exhibitions", path: "/exhibitions" },
    { label: "Art Consultation", path: "/art-consultation" },
    { label: "Picture Framing", path: "/picture-framing" },
    { label: "About", path: "/about" }
  ];

  const topNavLinks = [
    { label: "Contact", path: "/contact" }
  ];

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`w-full bg-background/80 backdrop-blur-xl border-b border-border sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-sm' : ''
      }`}
    >
      {/* Top bar */}
      <div className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_creative-showcase-578/artifacts/nx7nqxgj_1_ZEBRA-ONE-gallery-LOGO.png"
              alt="Zebra One Gallery"
              className="h-10 w-10 sm:h-14 sm:w-14"
            />
            <div className="hidden md:block">
              <div className="text-sm font-sans font-semibold tracking-tight text-foreground">ZEBRA ONE GALLERY</div>
              <div className="text-xs text-muted-foreground tracking-wide font-sans">EST. 1976 • HAMPSTEAD</div>
            </div>
          </Link>

          {/* Desktop search & actions */}
          <div className="hidden lg:flex items-center gap-6 flex-1 ml-6">
            <motion.form 
              className="relative w-full max-w-[400px]"
              whileFocus={{ scale: 1.02 }}
              onSubmit={submitSearch}
              role="search"
              data-testid="desktop-search-form"
            >
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by artist, gallery, style, theme, tag, etc."
                className="w-full px-4 py-2 pr-10 border border-border rounded-sm focus:outline-none focus:border-foreground text-sm transition-all bg-background font-sans"
                aria-label="Search artworks and artists"
                data-testid="desktop-search-input"
              />
              <button
                type="submit"
                aria-label="Search"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:text-foreground transition-colors"
                data-testid="desktop-search-submit"
              >
                <Search className="w-4 h-4 text-muted-foreground" />
              </button>
            </motion.form>

            <div className="flex items-center gap-3 ml-auto">
              {topNavLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.path}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors font-sans whitespace-nowrap"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 hover:bg-muted rounded-sm transition-colors"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-foreground" />
            ) : (
              <Menu className="w-6 h-6 text-foreground" />
            )}
          </button>
        </div>
      </div>

      {/* Desktop navigation */}
      <div className="hidden lg:block max-w-[1400px] mx-auto px-6">
        <nav className="flex items-center gap-6 py-3">
          {navigationLinks.map((link) => (
            <Link
              key={link.label}
              to={link.path}
              className={`text-sm font-sans whitespace-nowrap transition-all relative group text-foreground ${
                link.featured ? 'font-semibold' : ''
              }`}
            >
              <motion.span
                whileHover={{ y: -2 }}
                className="inline-block"
              >
                {link.label}
              </motion.span>
              {location.pathname === link.path && (
                <motion.div
                  layoutId="activeNav"
                  className="absolute -bottom-3 left-0 right-0 h-0.5 bg-foreground"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
              <span className="absolute -bottom-3 left-0 right-0 h-0.5 bg-foreground scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </Link>
          ))}
        </nav>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden border-t border-border bg-background overflow-hidden"
          >
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
              {/* Mobile search */}
              <form
                className="relative mb-6"
                onSubmit={submitSearch}
                role="search"
                data-testid="mobile-search-form"
              >
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search artworks, artists..."
                  className="w-full px-4 py-3 pr-12 border border-border rounded-sm focus:outline-none focus:border-foreground text-sm transition-all bg-background font-sans"
                  aria-label="Search artworks and artists"
                  data-testid="mobile-search-input"
                  autoFocus
                />
                <button
                  type="submit"
                  aria-label="Search"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 hover:text-foreground transition-colors"
                  data-testid="mobile-search-submit"
                >
                  <Search className="w-4 h-4 text-muted-foreground" />
                </button>
              </form>

              {/* Mobile navigation links */}
              <nav className="space-y-1 mb-6">
                {navigationLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.path}
                    className={`block px-4 py-3 text-base font-sans transition-colors rounded-sm ${
                      location.pathname === link.path
                        ? 'bg-muted text-foreground font-medium'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>

              {/* Mobile actions */}
              <div className="space-y-3 pt-6 border-t border-border">
                {topNavLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.path}
                    className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-sans"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
